import React from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Button,
  Chip,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  Close as CloseIcon,
  HourglassEmpty as HourglassIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import TranscricaoProgresso from './TranscricaoProgresso';
import {
  JobTranscricao,
  MAX_JOBS_SIMULTANEOS,
  formatarTempoRestante,
  isJobEmAndamento,
  isJobNaFila,
  nomeLote,
} from '../services/transcricao';
import { colors } from '../theme/expertiseTheme';

/**
 * Acompanhamento de várias pastas: as que estão sendo processadas (até MAX_JOBS_SIMULTANEOS)
 * e as que aguardam na fila. Cada pasta em processamento pode ser expandida para ver os arquivos.
 */
type Props = {
  jobs: JobTranscricao[];
  onCancelar: (job: JobTranscricao) => void;
  cancelandoId: string | null;
  expandidoId: string | null;
  onExpandir: (jobId: string | null) => void;
};

const ordinal = (n?: number | null) => (n ? `${n}º` : '—');

const TranscricaoFila: React.FC<Props> = ({
  jobs,
  onCancelar,
  cancelandoId,
  expandidoId,
  onExpandir,
}) => {
  const emProcessamento = jobs.filter(isJobEmAndamento);
  const naFila = jobs
    .filter(isJobNaFila)
    .sort((a, b) => (a.posicaoFila ?? 0) - (b.posicaoFila ?? 0));

  return (
    <Box>
      {/* Em processamento */}
      <Paper
        elevation={0}
        sx={{ border: `1px solid ${colors.border}`, borderRadius: '16px', overflow: 'hidden', mb: 3 }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: colors.dark }}>
            Em processamento
          </Typography>
          <Chip
            size="small"
            label={`${emProcessamento.length} de ${MAX_JOBS_SIMULTANEOS}`}
            sx={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: colors.info, fontWeight: 600 }}
          />
        </Box>

        {emProcessamento.length === 0 && (
          <Typography variant="body2" sx={{ color: colors.text.secondary, px: 3, py: 3 }}>
            Nenhuma pasta em processamento no momento.
          </Typography>
        )}

        {emProcessamento.map((job) => {
          const expandido = expandidoId === job.id;
          const listando = job.status === 'Pendente';
          const progresso = Math.min(100, Math.max(0, Math.round(job.progresso || 0)));
          const cancelando = cancelandoId === job.id;

          return (
            <Box key={job.id} sx={{ borderBottom: `1px solid ${colors.border}`, '&:last-of-type': { borderBottom: 0 } }}>
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: { xs: 'wrap', md: 'nowrap' },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.dark }} noWrap>
                    {nomeLote(job)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                    {listando
                      ? 'Listando arquivos da pasta…'
                      : `${job.arquivosConcluidos} de ${job.totalArquivos} arquivo${job.totalArquivos !== 1 ? 's' : ''}` +
                        (job.arquivosComErro ? ` · ${job.arquivosComErro} com erro` : '')}
                  </Typography>
                </Box>

                <Box sx={{ flex: 2, minWidth: 200, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <LinearProgress
                    variant={listando ? 'indeterminate' : 'determinate'}
                    value={progresso}
                    sx={{
                      flex: 1,
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: colors.light,
                      '& .MuiLinearProgress-bar': { backgroundColor: colors.primary, borderRadius: 4 },
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: colors.text.secondary, fontWeight: 600, minWidth: 44, textAlign: 'right' }}
                  >
                    {listando ? '' : `${progresso}%`}
                  </Typography>
                </Box>

                {!listando && (
                  <Tooltip title="Tempo estimado restante">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 120 }}>
                      <ScheduleIcon sx={{ fontSize: 16, color: colors.text.light }} />
                      <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                        {formatarTempoRestante(job.tempoRestanteSegundos)}
                      </Typography>
                    </Box>
                  </Tooltip>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  {!expandido && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<CloseIcon />}
                      disabled={cancelando}
                      onClick={() => onCancelar(job)}
                    >
                      {cancelando ? 'Cancelando...' : 'Cancelar'}
                    </Button>
                  )}
                  <IconButton
                    size="small"
                    aria-label={expandido ? 'Recolher detalhes' : 'Ver detalhes'}
                    aria-expanded={expandido}
                    onClick={() => onExpandir(expandido ? null : job.id)}
                    sx={{
                      transform: expandido ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.2s',
                    }}
                  >
                    <ExpandIcon />
                  </IconButton>
                </Box>
              </Box>

              <Collapse in={expandido} unmountOnExit>
                <Box sx={{ px: { xs: 2, md: 3 }, pb: 3, backgroundColor: 'rgba(0,0,0,0.015)' }}>
                  <TranscricaoProgresso
                    job={job}
                    onCancelar={() => onCancelar(job)}
                    cancelando={cancelando}
                  />
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Paper>

      {/* Na fila */}
      {naFila.length > 0 && (
        <Paper
          elevation={0}
          sx={{ border: `1px solid ${colors.border}`, borderRadius: '16px', overflow: 'hidden', mb: 3 }}
        >
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.dark }}>
              Na fila ({naFila.length})
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              Começam automaticamente quando abrir vaga
            </Typography>
          </Box>

          {naFila.map((job) => {
            const cancelando = cancelandoId === job.id;
            return (
              <Box
                key={job.id}
                sx={{
                  px: 3,
                  py: 1.75,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  borderBottom: `1px solid ${colors.border}`,
                  '&:last-of-type': { borderBottom: 0 },
                }}
              >
                <Chip
                  size="small"
                  label={ordinal(job.posicaoFila)}
                  sx={{ minWidth: 44, fontWeight: 700, backgroundColor: colors.light, color: colors.dark }}
                />
                <HourglassIcon sx={{ fontSize: 18, color: colors.text.light }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.dark }} noWrap>
                    {nomeLote(job)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                    Aguardando vaga
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  startIcon={<CloseIcon />}
                  disabled={cancelando}
                  onClick={() => onCancelar(job)}
                  sx={{ color: colors.text.secondary, flexShrink: 0 }}
                >
                  {cancelando ? 'Removendo...' : 'Remover da fila'}
                </Button>
              </Box>
            );
          })}
        </Paper>
      )}
    </Box>
  );
};

export default TranscricaoFila;
